import { Check, AlertCircle, Loader2, Mail } from 'lucide-react';
import { FormInput, StatusIndicator } from './shared';
import type { RegisterFormData, FormErrors, IdentityVerificationState } from '../../../../stores/registerStore';

/**
 * Step 2: 信息填写 + 双重验证（五步制合并步）
 * - 信息字段唯一性 blur 检查
 * - 邮箱验证码：发送（60s 限发）+ 6 位码输入
 * - 三要素验证状态内联展示：提交时由主控制器自动触发，结果回显在此
 */
export function Step2InfoVerify({
  formData,
  errors,
  setFormField,
  validateField,
  usernameStatus,
  emailStatus,
  phoneStatus,
  backupPhoneStatus,
  idCardStatus,
  identityVerification,
  emailCodeCountdown,
  emailCodeSending,
  emailCodeSentTo,
  onUsernameBlur,
  onEmailBlur,
  onPhoneBlur,
  onBackupPhoneBlur,
  onIdCardBlur,
  onSendEmailCode,
}: {
  formData: RegisterFormData;
  errors: FormErrors;
  setFormField: (field: keyof RegisterFormData, value: string) => void;
  validateField: (field: keyof RegisterFormData) => boolean;
  usernameStatus: 'idle' | 'checking' | 'ok' | 'taken' | 'customer';
  emailStatus: 'idle' | 'checking' | 'ok' | 'taken' | 'customer';
  phoneStatus: 'idle' | 'checking' | 'ok' | 'taken' | 'customer';
  backupPhoneStatus: 'idle' | 'checking' | 'ok' | 'taken' | 'customer';
  idCardStatus: 'idle' | 'checking' | 'ok' | 'taken' | 'customer';
  identityVerification: IdentityVerificationState;
  emailCodeCountdown: number;
  emailCodeSending: boolean;
  emailCodeSentTo: string;
  onUsernameBlur: () => void;
  onEmailBlur: () => void;
  onPhoneBlur: () => void;
  onBackupPhoneBlur: () => void;
  onIdCardBlur: () => void;
  onSendEmailCode: () => void;
}) {

  return (
    <div className="space-y-4">
      <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
        <p className="text-sm text-amber-700">
          请填写真实有效的个人信息并完成双重验证（实名核验 + 邮箱验证码）
        </p>
      </div>

      <FormInput
        label="用户名"
        value={formData.username}
        onChange={(value) => setFormField('username', value)}
        onBlur={onUsernameBlur}
        error={errors.username}
        placeholder="请设置用户名（3-20个字符）"
        required
        validate={() => validateField('username')}
        rightElement={<StatusIndicator status={usernameStatus} />}
      />

      <FormInput
        label="真实姓名"
        value={formData.fullName}
        onChange={(value) => setFormField('fullName', value)}
        onBlur={() => validateField('fullName')}
        error={errors.fullName}
        placeholder="请输入身份证上的法定姓名"
        required
        validate={() => validateField('fullName')}
      />

      <FormInput

        label="联系电话"
        value={formData.phone}
        onChange={(value) => setFormField('phone', value)}
        onBlur={onPhoneBlur}
        error={errors.phone}
        placeholder="请输入手机号（作为身份锚点）"
        type="tel"
        inputMode="tel"
        required
        validate={() => validateField('phone')}
        rightElement={<StatusIndicator status={phoneStatus} />}
      />

      <FormInput
        label="备用电话（选填）"
        value={formData.backupPhone}
        onChange={(value) => setFormField('backupPhone', value)}
        onBlur={onBackupPhoneBlur}
        error={errors.backupPhone}
        placeholder="请输入备用电话"
        type="tel"
        inputMode="tel"
        validate={() => validateField('backupPhone')}
        rightElement={<StatusIndicator status={backupPhoneStatus} />}
      />

      <FormInput
        label="身份证号"
        value={formData.idCard}
        onChange={(value) => setFormField('idCard', value)}
        onBlur={onIdCardBlur}
        error={errors.idCard}
        placeholder="请输入身份证号（仅用于身份唯一性核验）"
        required
        validate={() => validateField('idCard')}
        rightElement={<StatusIndicator status={idCardStatus} />}
      />

      {/* 邮箱 + 验证码 */}
      <FormInput
        label="邮箱"
        value={formData.email}
        onChange={(value) => setFormField('email', value)}
        onBlur={onEmailBlur}
        error={errors.email}
        placeholder="请输入邮箱地址（必填，用于找回密码）"
        type="email"
        inputMode="email"
        required
        validate={() => validateField('email')}
        rightElement={
          <button
            type="button"
            onClick={onSendEmailCode}
            disabled={emailCodeSending || emailCodeCountdown > 0 || emailStatus === 'taken' || !!errors.email}
            className="px-3 py-1.5 text-xs font-medium rounded-md border transition-colors whitespace-nowrap disabled:cursor-not-allowed
              bg-teal-50 text-teal-700 border-teal-200 hover:bg-teal-100
              disabled:bg-slate-50 disabled:text-slate-400 disabled:border-slate-200"
          >
            {emailCodeSending ? (
              <span className="flex items-center gap-1">
                <Loader2 className="w-3 h-3 animate-spin" />
                发送中
              </span>
            ) : emailCodeCountdown > 0 ? (
              `${emailCodeCountdown}s 后重发`
            ) : (
              '发送验证码'
            )}
          </button>
        }
      />

      <FormInput
        label="邮箱验证码"
        value={formData.emailCode}
        onChange={(value) => setFormField('emailCode', value.replace(/\D/g, '').slice(0, 6))}
        error={errors.emailCode}
        placeholder={emailCodeSentTo ? `验证码已发送至 ${emailCodeSentTo}` : '请先获取 6 位邮箱验证码'}
        inputMode="numeric"
        maxLength={6}
        validate={() => validateField('emailCode')}
      />

      {/* 三要素验证状态（内联回显） */}
      {identityVerification.isLoading && (
        <div className="flex items-center justify-center py-4 px-5 bg-blue-50 border border-blue-200 rounded-lg">
          <Loader2 className="w-5 h-5 text-teal-500 animate-spin mr-3" />
          <span className="text-sm text-slate-600">正在进行实名三要素核验...</span>
        </div>
      )}

      {identityVerification.error && !identityVerification.isLoading && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm text-red-700 font-medium">实名核验未通过</p>
              <p className="text-sm text-red-600 mt-1">{identityVerification.error}</p>
              <p className="text-xs text-slate-500 mt-2">请核对姓名、身份证号、手机号后重试；多次失败可申请人工审核</p>
            </div>
          </div>
        </div>
      )}

      {identityVerification.isVerified && !identityVerification.isLoading && (
        <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex items-start gap-3">
            <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
            <div className="flex items-center gap-2">
              <Mail className="w-4 h-4 text-green-600" />
              <p className="text-sm text-green-700 font-medium">实名核验通过，邮箱验证码校验将在提交注册时完成</p>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}